import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import type { ArtistType, MemberType } from '../../types/ArtistType';
import { Plus, Xmark } from '@peacecover/icon';

interface CollectionSummaryBarProps {
  totalCount: number;
  collectedCount: number;
  currentMember: MemberType | null;
  showFabButton: boolean;
  selectedArtist: ArtistType | null;
}

const CollectionSummaryBar = ({
  totalCount,
  collectedCount,
  currentMember,
  showFabButton,
  selectedArtist
}: CollectionSummaryBarProps) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const percentage =
    totalCount > 0 ? ((collectedCount / totalCount) * 100).toFixed(1).replace('.0', '') : '0';
  const title = currentMember ? `${currentMember.name} ${t('dBall')} ${percentage}%` : '';

  const handleAddPhotoCards = () => {
    navigate('/collections/select-method');
    setIsExpanded(false);
  };

  const handleReportPhotoCard = () => {
    openNewTab(`/report/select-member?artistId=${selectedArtist?.artistId}&reportType=photoCard`);
    setIsExpanded(false);
  };

  return (
    <>
      {showFabButton && isExpanded && (
        <>
          {/* 외부 클릭 시 닫기 */}
          <div className="fixed inset-0 z-[98]" onClick={() => setIsExpanded(false)} role="none" />
          {/* 팝업 옵션 버튼들 */}
          <div
            className="fixed left-0 right-0 z-[101] mx-auto flex max-w-[var(--chakra-sizes-xl)] flex-col items-end gap-[8px] px-[24px]"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 145px)' }}
          >
            <button
              type="button"
              onClick={handleReportPhotoCard}
              className="px-[18px] py-[15px] rounded-full bg-primary-light dark:bg-primary-dark text-white font-semibold text-[16px] leading-[19px] whitespace-nowrap"
            >
              {t('CollectionsHomePage.ReportPhotoCardFabButton')}
            </button>
            <button
              type="button"
              onClick={handleAddPhotoCards}
              className="px-[18px] py-[15px] rounded-full bg-primary-light dark:bg-primary-dark text-white font-semibold text-[16px] leading-[19px] whitespace-nowrap"
            >
              {t('CollectionsHomePage.EditPhotoCardFabButton')}
            </button>
          </div>
        </>
      )}

      <div
        className="fixed left-0 right-0 max-w-[var(--chakra-sizes-xl)] mx-auto z-[100] bg-background-elevated rounded-t-[20px] shadow-[0_-2px_8px_0_rgba(0,0,0,0.05)] px-[24px] py-[18px]"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 65px)' }}
      >
        <div className="flex flex-row items-center gap-[12px]">
          <MemberProgressRing percentage={Number.parseFloat(percentage)} />
          <div className="flex flex-col gap-[4px] flex-1 min-w-0">
            <span className="text-text-main-lv2 font-semibold text-[15px] leading-[18px]">
              {title}
            </span>
            <span className="text-text-main-lv3 font-medium text-[13px] leading-[16px]">
              {t('CollectionsHomePage.CollectedPhotoCards', { count: collectedCount })}
            </span>
          </div>
          {showFabButton &&
            (isExpanded ? (
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="shrink-0 w-[49px] h-[49px] rounded-full bg-primary-light dark:bg-primary-dark text-white flex items-center justify-center"
              >
                <Xmark size={24} className="text-white" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="flex items-center gap-[4px] shrink-0 pl-[14px] pr-[18px] py-[11px] rounded-full bg-primary-light dark:bg-primary-dark text-white font-semibold text-[14px] whitespace-nowrap"
              >
                <Plus size={15} />
                {t('CollectionsHomePage.FillPhotoCardFabButton')}
              </button>
            ))}
        </div>
      </div>
    </>
  );
};

export default CollectionSummaryBar;

interface MemberProgressRingProps {
  percentage: number;
}

const SIZE = 50;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const MemberProgressRing = ({ percentage }: MemberProgressRingProps) => {
  const { colorMode } = useColorMode();
  const offset = CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE;

  return (
    <div className="relative w-[50px] h-[50px] shrink-0">
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="absolute inset-0 -rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          stroke={colorMode === 'dark' ? '#2C2A46' : '#DCD6FA'}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="#5C3DFA"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-[3px] rounded-full overflow-hidden bg-white dark:bg-background-elevated flex items-center justify-center text-[20px] leading-none">
        🔮
      </div>
    </div>
  );
};
